const COMPLETE_VERIFICATION = new Set(['approved', 'already_verified', 'completed']);
const COMPLETE_LOGISTICS = new Set(['completed', 'cancelled', 'shipped']);
const TERMINAL_VERIFICATION = new Set(['rejected', 'cancelled', 'refunded', 'reversed', 'archived']);
const IDENTITY_DOCUMENTS = new Set(['id_front', 'id_back', 'selfie']);
const BUSINESS_DOCUMENTS = new Set(['business_doc', 'gewerbeanmeldung', 'handelsregisterauszug', 'vollmacht']);
const BUSINESS_CUSTOMER_TYPES = new Set([
  'unternehmen', 'gewerbe', 'einzelunternehmen', 'kapitalgesellschaft', 'verein', 'ausland', 'gruendung',
]);
export const VERIFICATION_REVIEW_FILTERS = [
  { key: 'all', label: 'Alle offenen' },
  { key: 'identity_review', label: 'Identität prüfen' },
  { key: 'business_review', label: 'Unternehmen prüfen' },
  { key: 'followup_answered', label: 'Nachforderung beantwortet' },
  { key: 'no_upload', label: 'Noch nichts hochgeladen' },
  { key: 'stale', label: 'Lange unbearbeitet' },
];

const normalized = (value) => String(value || '').trim().toLowerCase();
const validTime = (value) => {
  const time = new Date(value || '').getTime();
  return Number.isFinite(time) ? time : null;
};

function verificationDocumentTypes(row = {}) {
  const eventTypes = Array.isArray(row.latest_upload_event?.metadata?.document_types)
    ? row.latest_upload_event.metadata.document_types
    : [];
  return new Set([
    ...(Array.isArray(row.documents) ? row.documents.map((document) => document?.document_type) : []),
    ...eventTypes,
  ].map(normalized).filter(Boolean));
}

function reviewCustomerLabel(row = {}) {
  const customer = row.customers || {};
  return customer.company_name || customer.name || `Fall #${row.id}`;
}

export function buildVerificationReviewWorklist(verifications = [], { now = Date.now(), staleHours = 72 } = {}) {
  const staleMs = staleHours * 60 * 60 * 1000;

  return verifications.flatMap((row) => {
    const identity = normalized(row.identity_status);
    const business = normalized(row.business_status);
    const archiveStatus = normalized(row.archive_status || 'active');
    const businessRelevant = BUSINESS_CUSTOMER_TYPES.has(normalized(row.customers?.customer_type));
    const identityDone = COMPLETE_VERIFICATION.has(identity);
    const businessDone = !businessRelevant || COMPLETE_VERIFICATION.has(business) || business === 'not_required';

    if (archiveStatus !== 'active' || TERMINAL_VERIFICATION.has(identity) || TERMINAL_VERIFICATION.has(business)) return [];
    if (identityDone && businessDone) return [];

    const documents = Array.isArray(row.documents) ? row.documents : [];
    const documentTypes = verificationDocumentTypes(row);
    const hasUpload = documents.length > 0 || Boolean(row.latest_document_uploaded_at);
    const hasIdentityUpload = [...documentTypes].some((type) => IDENTITY_DOCUMENTS.has(type));
    const hasBusinessUpload = [...documentTypes].some((type) => BUSINESS_DOCUMENTS.has(type));
    const followupAt = validTime(row.followup_request?.created_at);
    const uploadAt = validTime(row.latest_document_uploaded_at);
    const followupAnswered = Boolean(
      followupAt && uploadAt && uploadAt > followupAt
    ) || normalized(row.latest_upload_event?.event_type) === 'requested_followup';
    const identityReview = !identityDone && hasIdentityUpload && ['new', 'submitted', 'in_review', 'additional_requested', 'pending'].includes(identity);
    const businessReview = businessRelevant && !businessDone && hasBusinessUpload && ['new', 'submitted', 'in_review', 'additional_requested', 'pending'].includes(business);
    const waitingSince = uploadAt || validTime(row.updated_at) || validTime(row.created_at) || now;
    const waitingMs = Math.max(0, now - waitingSince);
    const stale = waitingMs >= staleMs;
    const filters = [
      identityReview && 'identity_review',
      businessReview && 'business_review',
      followupAnswered && 'followup_answered',
      !hasUpload && 'no_upload',
      stale && 'stale',
    ].filter(Boolean);

    let nextAction = 'Fallstatus prüfen';
    if (followupAnswered) nextAction = 'Nachgereichte Unterlagen prüfen';
    else if (identityReview && businessReview) nextAction = 'Identität und Unternehmen prüfen';
    else if (identityReview) nextAction = 'Identitätsunterlagen prüfen';
    else if (businessReview) nextAction = 'Unternehmensunterlagen prüfen';
    else if (!hasUpload) nextAction = 'Auf Kundeneinreichung warten';

    return [{
      id: `verification-review-${row.id}`,
      verificationId: row.id,
      customerLabel: reviewCustomerLabel(row),
      filters,
      identityReview,
      businessReview,
      followupAnswered,
      hasUpload,
      latestUploadAt: row.latest_document_uploaded_at || null,
      waitingSince: new Date(waitingSince).toISOString(),
      waitingMs,
      nextAction,
    }];
  }).sort((left, right) => {
    const leftUploaded = left.identityReview || left.businessReview || left.followupAnswered;
    const rightUploaded = right.identityReview || right.businessReview || right.followupAnswered;
    if (leftUploaded !== rightUploaded) return leftUploaded ? -1 : 1;
    return validTime(left.waitingSince) - validTime(right.waitingSince);
  });
}

export function filterVerificationReviewWorklist(items = [], filter = 'all') {
  return filter === 'all' ? items : items.filter((item) => item.filters.includes(filter));
}

function customerLabel(row = {}) {
  const customer = row.customers || row.customer || {};
  return customer.company_name || customer.name || customer.email || row.customer_name || row.customer_email || 'Kunde';
}

export function buildOpenOperations({
  systemStatus,
  verifications = [],
  portalRequests = [],
  logisticsRequests = [],
  accessReviews = [],
  nextcloudInventory,
  notifications = [],
} = {}) {
  const items = [];

  (systemStatus?.checks || []).filter((check) => check.status === 'error').forEach((check) => items.push({
    id: `system-${check.id}`, priority: 'critical', area: 'system-status', category: 'System',
    title: check.label, detail: check.message || 'Systemprüfung fehlgeschlagen.', created_at: systemStatus.checked_at,
  }));

  portalRequests.filter((row) => String(row.status || '').toLowerCase() === 'pending').forEach((row) => items.push({
    id: `portal-${row.id}`, priority: row.request_type === 'subscription_withdrawal' ? 'high' : 'normal',
    area: 'portal-requests', category: 'Portal-Anfrage', title: customerLabel(row),
    detail: row.request_type === 'subscription_withdrawal' ? 'Widerruf oder Rückabwicklung prüfen' : 'Kundenanfrage prüfen',
    created_at: row.created_at,
  }));

  logisticsRequests.filter((row) => !COMPLETE_LOGISTICS.has(String(row.status || '').toLowerCase())).forEach((row) => items.push({
    id: `logistics-${row.id}`, priority: ['shipping_costs_open', 'paid'].includes(row.status) ? 'high' : 'normal',
    area: 'logistics', category: 'Versand', title: customerLabel(row),
    detail: row.status === 'shipping_costs_open' ? 'Tatsächliche Versandkosten fehlen' : `Status: ${row.status || 'neu'}`,
    created_at: row.created_at,
  }));

  verifications.filter((row) => {
    const identity = String(row.identity_status || '').toLowerCase();
    const business = String(row.business_status || '').toLowerCase();
    return !COMPLETE_VERIFICATION.has(identity) || !COMPLETE_VERIFICATION.has(business);
  }).forEach((row) => items.push({
    id: `verification-${row.id}`, priority: row.followup_request?.created_at ? 'high' : 'normal',
    area: 'dashboard-focus', category: 'Verifizierung', title: customerLabel(row),
    detail: row.followup_request?.created_at ? 'Nachforderung oder Rückmeldung offen' : 'Verifizierung noch nicht abgeschlossen',
    created_at: row.updated_at || row.created_at,
  }));

  accessReviews.filter((row) => ['pending_confirmation', 'failed'].includes(row.status)).forEach((row) => items.push({
    id: `access-${row.customer_id}`, priority: row.status === 'failed' ? 'critical' : 'high',
    area: 'access-reviews', category: 'Zugang', title: customerLabel(row),
    detail: row.status === 'failed' ? 'Zugangsänderung fehlgeschlagen' : 'Deaktivierung bewusst prüfen',
    created_at: row.updated_at || row.eligible_at,
  }));

  (nextcloudInventory?.unassigned_accounts || []).forEach((row) => items.push({
    id: `nextcloud-${row.user_id || row.id}`, priority: 'high', area: 'access-reviews',
    category: 'Nextcloud', title: row.display_name || row.user_id || 'Nicht zugeordnetes Konto',
    detail: 'Nextcloud-Konto ist keinem Kunden eindeutig zugeordnet', created_at: row.last_login || null,
  }));

  notifications.filter((row) => !row.is_done && ['error', 'critical'].includes(String(row.severity || '').toLowerCase())).forEach((row) => items.push({
    id: `notification-${row.id}`, priority: 'critical', area: 'activity', category: 'Fehler',
    title: row.title || 'Systemvorgang fehlgeschlagen', detail: row.message || 'Manuelle Prüfung erforderlich',
    created_at: row.created_at,
  }));

  const rank = { critical: 0, high: 1, normal: 2 };
  return items.sort((left, right) => (rank[left.priority] - rank[right.priority]) ||
    (new Date(left.created_at || 0).getTime() - new Date(right.created_at || 0).getTime()));
}
